import React, { useEffect, useState } from 'react';
import { getAuditLogs, getAuditDetail } from '../api/api';
import { Modal, ErrorState, EmptyState, TableSkeleton, SearchableSelect, Message } from '../components';
import { PageHeader, Panel, Toolbar } from '../components/layout';
import './AuditLogsPage.css';

const labels = { pending: '处理中', success: '成功', failed: '失败', submitted: '已提交' };
const tones = { pending: 'warning', success: 'success', failed: 'danger', submitted: 'info' };
const Status = ({ value }) => <span className={`badge badge-${tones[value] || 'default'}`}>{labels[value] || value}</span>;
const initial = { actor: '', module: '', status: '', start: '', end: '' };
const formatTime = value => new Date(value).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
const json = value => JSON.stringify(value, null, 2);

export default function AuditLogsPage() {
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(initial);
  const [query, setQuery] = useState({ page: 1 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getAuditLogs(query).then(response => { if (active) setData(response.data); })
      .catch(() => { if (active) setError('审计记录加载失败'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, revision]);

  useEffect(() => {
    if (!selected) return undefined;
    let active = true;
    setDetail(null);
    setDetailError(null);
    getAuditDetail(selected).then(response => { if (active) setDetail(response.data); })
      .catch(() => { if (active) setDetailError('审计详情加载失败'); });
    return () => { active = false; };
  }, [selected, revision]);

  const apply = event => {
    event.preventDefault();
    if (draft.start && draft.end && draft.start > draft.end) {
      setNotice('开始时间不能晚于结束时间');
      return;
    }
    const next = Object.fromEntries(Object.entries(draft).filter(([, value]) => value));
    // Inputs are interpreted as Shanghai time, independent of browser timezone.
    ['start', 'end'].forEach(key => { if (next[key]) next[key] += ':00+08:00'; });
    setQuery({ ...next, page: 1 });
  };

  const reset = () => { setDraft(initial); setQuery({ page: 1 }); };
  const filtered = Object.keys(query).some(key => key !== 'page');
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / (data?.page_size || 20)));

  return <div className="audit-page">
    <PageHeader title="用户操作审计" actions={<button type="button" className="btn btn-secondary" disabled={loading} onClick={() => setRevision(v => v + 1)}>{loading && data ? '刷新中…' : '刷新'}</button>} />
    <form onSubmit={apply}>
      <Toolbar className="page-toolbar" actions={<>
        <button className="btn btn-primary" disabled={loading}>{loading ? '查询中…' : '查询'}</button>
        <button type="button" className="btn btn-secondary" disabled={loading} onClick={reset}>重置</button>
      </>}>
        {[['actor', '操作者', '姓名、邮箱或 ID'], ['module', '模块', '如 rules、ka'], ['start', '开始时间'], ['end', '结束时间']].map(([key, label, placeholder]) =>
          <div className="form-group audit-filter" key={key}>
            <label className="form-label" htmlFor={`audit-${key}`}>{label}</label>
            <input id={`audit-${key}`} className="form-input" placeholder={placeholder} type={['start', 'end'].includes(key) ? 'datetime-local' : 'text'} value={draft[key]} onChange={e => setDraft({ ...draft, [key]: e.target.value })} />
          </div>)}
        <div className="form-group audit-filter">
          <span className="form-label">结果</span>
          <SearchableSelect value={draft.status} options={[{ value: '', label: '全部结果' }, ...Object.entries(labels).map(([value, label]) => ({ value, label }))]} searchable={false} onChange={status => setDraft({ ...draft, status })} />
        </div>
      </Toolbar>
    </form>
    <Panel title="审计记录" subtitle="仅保留最近 30 天的页面访问、提交操作及已生效的数据变更。历史页面访问无法补录。" aria-busy={loading}>
      {error && <ErrorState title={error} onRetry={() => setRevision(v => v + 1)} />}
      {!data && loading ? <TableSkeleton columnCount={6} /> : data && <>
        {data.items.length ? <div className="table-container"><table className="table"><thead><tr>{['时间（上海）', '操作者', '模块', '操作', '结果', '详情'].map(label => <th key={label}>{label}</th>)}</tr></thead>
          <tbody>{data.items.map(row => <tr key={row.id}>
            <td className="audit-time">{formatTime(row.created_at)}</td>
            <td><strong>{row.actor_name || '未认证用户'}</strong><div className="audit-secondary">{row.actor_email || '—'}</div></td>
            <td>{row.module}</td><td className="audit-action">{row.action}</td><td><Status value={row.status} /></td>
            <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => { setDetail(null); setDetailError(null); setSelected(row.id); }}>查看</button></td>
          </tr>)}</tbody>
        </table></div> : <EmptyState title={filtered ? '没有符合条件的审计记录' : '暂无审计记录'} description={filtered ? '请调整筛选条件后重试。' : '新访问或提交操作后，可刷新查看记录。'} action={filtered && <button className="btn btn-secondary btn-sm" onClick={reset}>清空筛选</button>} />}
        <div className="pagination">
          <div className="pagination-info">共 {data.total} 条记录，第 {data.page || query.page} / {totalPages} 页</div>
          <div className="pagination-controls">
            <button type="button" className="btn btn-secondary btn-sm" disabled={loading || query.page <= 1} onClick={() => setQuery({ ...query, page: query.page - 1 })}>上一页</button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={loading || query.page >= totalPages} onClick={() => setQuery({ ...query, page: query.page + 1 })}>下一页</button>
          </div>
        </div>
      </>}
    </Panel>
    {notice && <Message type="error" message={notice} onClose={() => setNotice(null)} />}
    <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="操作审计详情" size="large">
      {detailError ? <ErrorState title={detailError} onRetry={() => setRevision(v => v + 1)} /> : !detail ? <TableSkeleton /> : <>
        <p>{detail.actor_name || '未认证用户'} · {detail.actor_email || '—'} · {formatTime(detail.created_at)}</p>
        <p>{detail.action} · <Status value={detail.status} /> · HTTP {detail.status_code}</p>
        <p>{detail.method} {detail.path}</p>
        <p>{detail.result?.message}{detail.result?.reason ? `：${detail.result.reason}` : ''}</p>
        {detail.execution && <p>关联执行 #{detail.execution.log_id} · 当前状态：{detail.execution.status}</p>}
        {detail.result?.task_id && <p>关联任务 #{detail.result.task_id} · 当前状态：{detail.task?.status || "不可用"}</p>}
        {detail.result?.run_id && <p>关联运行 #{detail.result.run_id} · 当前状态：{detail.run?.status || "不可用"}</p>}
        {detail.executions?.map(item => <p key={item.log_id}>关联执行 #{item.log_id} · 当前状态：{item.status}</p>)}
        {detail.result?.requested_revision != null && <p>重新汇总请求版本：{detail.result.requested_revision}</p>}
        {!detail.changes.length && <p>此操作没有已提交的数据变更。</p>}
        {detail.changes.map((change, index) => <Panel key={index} className="audit-change" title={`${change.resource} · ${change.resource_id}`} subtitle={`变更字段：${change.changed_fields.join('、')}`}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {['before', 'after'].map(key => <div key={key}><strong>{key === 'before' ? '修改前' : '修改后'}</strong><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{json(change[key])}</pre></div>)}
          </div>
        </Panel>)}
      </>}
    </Modal>
  </div>;
}
